import { Fragment, KeyboardEvent, MouseEvent, ReactNode } from 'react';

import { ErrorIcon, WarningIcon } from '../Icon';

import './DetailsList.css';

export type HeaderHintIcon = 'error' | 'warning';

export interface DetailItemLink {
  /**
   * Optional aria-label for accessibility
   */
  ariaLabel?: string;
  /**
   * When true, the link stays visible but cannot be activated
   */
  disabled?: boolean;
  /**
   * Optional icon to display with the link
   */
  icon?: ReactNode;
  /**
   * Text label for the link
   */
  label: string;
  /**
   * Callback when the link is clicked. Not invoked when {@link disabled} is true.
   */
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Hover and focus text shown when the link is disabled
   */
  title?: string;
}

export interface DetailItem {
  /**
   * Optional alignment for the row content and action links
   */
  alignItems?: 'center' | 'flex-start';
  /**
   * Label text for the detail item
   */
  label: string;
  /**
   * Value to display for the detail item
   */
  value: string | number | ReactNode;
  /**
   * Optional array of action links for the detail item
   */
  links?: DetailItemLink[];
}

interface DetailsListProps {
  /**
   * Array of detail items to display
   */
  items: DetailItem[];
  /**
   * Optional header text for the details list section
   */
  header?: string;
  /**
   * CSS class name for the header element
   */
  headerClassName?: string;
  /**
   * Optional hint text displayed below the header
   */
  headerHint?: string;
  /**
   * Optional icon displayed before the header hint text
   */
  headerHintIcon?: HeaderHintIcon;
  /**
   * Whether the section is required (displays asterisk)
   */
  required?: boolean;
  /**
   * Whether to show borders on all items including the last one
   * Useful when multiple DetailsList components are stacked
   */
  showBorderOnLastItem?: boolean;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function handleDetailLinkClick(event: MouseEvent<HTMLAnchorElement>, link: DetailItemLink) {
  event.preventDefault();

  if (link.disabled) {
    return;
  }

  link.onClick(event);
}

function handleDetailLinkKeyDown(event: KeyboardEvent<HTMLAnchorElement>, isDisabled: boolean) {
  if (isDisabled && (event.key === 'Enter' || event.key === ' ')) {
    event.preventDefault();
  }
}

function renderHeaderHintIcon(headerHintIcon: HeaderHintIcon | undefined, baseTestId: string) {
  if (headerHintIcon === 'error') {
    return (
      <span
        aria-hidden='true'
        data-testid={`${baseTestId}-header-hint-icon`}>
        <ErrorIcon
          ariaLabel=''
          size={18}
        />
      </span>
    );
  }

  if (headerHintIcon === 'warning') {
    return (
      <span
        aria-hidden='true'
        data-testid={`${baseTestId}-header-hint-icon`}>
        <WarningIcon
          ariaLabel=''
          size={18}
        />
      </span>
    );
  }

  return null;
}

function DetailsList({
  items,
  header,
  headerClassName,
  headerHint,
  headerHintIcon,
  required,
  showBorderOnLastItem = false,
  'data-testid': dataTestId,
}: DetailsListProps) {
  const defaultHeaderClassName = 'ds-h2 ds-u-margin-top--3 ds-u-margin-bottom--1';
  const headerStyle = { marginBlock: 0 };
  const baseTestId = dataTestId ?? 'details-list';

  return (
    <div
      className='details-list'
      data-testid={baseTestId}>
      {header && (
        <h3
          className={headerClassName || defaultHeaderClassName}
          data-testid={`${baseTestId}-header`}
          id='details-list-header'
          style={headerStyle}>
          {header}
          {required && (
            <span
              aria-label='required'
              style={{ color: 'var(--color-error)' }}>
              *
            </span>
          )}
        </h3>
      )}
      {headerHint && (
        <p
          className='ds-text ds-u-margin-top--1 ds-u-margin-bottom--0 details-list__header-hint'
          data-testid={`${baseTestId}-header-hint`}>
          {renderHeaderHintIcon(headerHintIcon, baseTestId)}
          <span>{headerHint}</span>
        </p>
      )}
      <dl
        aria-labelledby={header ? 'details-list-header' : undefined}
        data-testid={`${baseTestId}-items`}
        style={{ margin: 0 }}>
        {items.map(({ alignItems = 'center', label, value, links }, index) => {
          const isLastItem = index === items.length - 1;
          const shouldShowBorder = !isLastItem || showBorderOnLastItem;
          const rowStyle = alignItems === 'center' ? { gap: '1rem' } : { alignItems, gap: '1rem' };
          const rowClassName =
            alignItems === 'center'
              ? 'ds-u-display--flex ds-u-justify-content--between ds-u-align-items--center'
              : 'ds-u-display--flex ds-u-justify-content--between';

          return (
            <div
              key={`${label}-${index}`}
              className={`ds-u-padding-y--2 ds-u-padding-left--2${shouldShowBorder ? ' ds-u-border-bottom--1' : ''}`}
              data-testid={`${baseTestId}-item-${index}`}>
              <div
                className={rowClassName}
                data-testid={`${baseTestId}-item-row-${index}`}
                style={rowStyle}>
                <div
                  data-testid={`${baseTestId}-item-content-${index}`}
                  style={{ flex: 1 }}>
                  <dt
                    className='ds-u-font-weight--bold'
                    data-testid={`${baseTestId}-item-label-${index}`}>
                    {label}
                  </dt>
                  <dd
                    data-testid={`${baseTestId}-item-value-${index}`}
                    style={{ margin: 0 }}>
                    {value}
                  </dd>
                </div>
                {links && links.length > 0 && (
                  <div
                    className='ds-u-display--flex ds-u-align-items--center ds-u-margin-right--2'
                    data-testid={`${baseTestId}-item-links-${index}`}>
                    {links.map((link, linkIndex) => {
                      const isDisabled = Boolean(link.disabled);
                      const tooltipId = `${baseTestId}-item-link-${index}-${linkIndex}-tooltip`;
                      const showDisabledTooltip = isDisabled && Boolean(link.title);

                      return (
                        <Fragment key={`${link.label}-${linkIndex}`}>
                          {linkIndex > 0 && (
                            <span
                              aria-hidden='true'
                              data-testid={`${baseTestId}-item-link-separator-${index}-${linkIndex}`}
                              style={{ color: 'var(--color-gray)', margin: '0 0.5rem' }}>
                              |
                            </span>
                          )}
                          <span
                            className='details-list__link-wrapper'
                            data-testid={`${baseTestId}-item-link-wrapper-${index}-${linkIndex}`}>
                            <a
                              aria-describedby={showDisabledTooltip ? tooltipId : undefined}
                              aria-disabled={isDisabled || undefined}
                              aria-label={link.ariaLabel}
                              className='details-list__link'
                              data-testid={`${baseTestId}-item-link-${index}-${linkIndex}`}
                              href={isDisabled ? undefined : '#'}
                              role={isDisabled ? 'link' : undefined}
                              tabIndex={isDisabled ? 0 : undefined}
                              onClick={(event) => handleDetailLinkClick(event, link)}
                              onKeyDown={(event) => handleDetailLinkKeyDown(event, isDisabled)}>
                              {link.icon && (
                                <span
                                  aria-hidden='true'
                                  data-testid={`${baseTestId}-item-link-icon-${index}-${linkIndex}`}
                                  style={{ display: 'inline-flex' }}>
                                  {link.icon}
                                </span>
                              )}
                              {link.label}
                            </a>
                            {showDisabledTooltip && (
                              <span
                                className='details-list__link-tooltip'
                                data-testid={`${baseTestId}-item-link-${index}-${linkIndex}-tooltip`}
                                id={tooltipId}
                                role='tooltip'>
                                {link.title}
                              </span>
                            )}
                          </span>
                        </Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

export default DetailsList;
